<template>
  <v-container>
    <!-- Header band -->
    <div class="topbar">
      <div class="topbar__row">
        <v-btn
          icon
          variant="text"
          :title="$filters.capitalize($t('machine.back_wind'))"
          :to="{ name: 'wind_production' }"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="topbar__badge">
          <v-icon size="22">mdi-factory</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">
            {{ stationName || $filters.capitalize($t("machine.station")) }}
          </div>
          <div class="topbar__sub">
            {{ items.length }} {{ $t("machine.producers", items.length) }}
            <template v-if="items.length"> &middot; {{ runningCount }} {{ $t("production.producers_running") }}</template>
            <template v-if="lastRefresh"> &middot; {{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
            v-model="range"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="range-toggle"
          >
            <v-btn v-for="r in ranges" :key="r.value" :value="r.value" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="power_station_id"
            variant="tonal"
            size="small"
            prepend-icon="mdi-chart-areaspline"
            :href="grafanaWindPowerStationUrl(power_station_id, range)"
            target="_blank"
            rel="noopener"
          >
            Grafana
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="load"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <template v-if="power_station_id">
      <!-- KPI tiles -->
      <div class="kpi-grid">
        <v-card flat class="kpi kpi--blue">
          <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.power")) }}</div>
            <div class="kpi__value">{{ $filters.formatFloat(totalPower) }} <span class="kpi__unit">{{ $t("unity.kw") }}</span></div>
            <div class="kpi__hint">{{ $t("production.theoretical") }} {{ $filters.formatFloat(totalTheoretical) }} {{ $t("unity.kw") }}</div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--purple">
          <div class="kpi__badge"><v-icon size="20">mdi-weather-windy</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.wind")) }}</div>
            <div class="kpi__value">{{ $filters.formatFloat(avgWind) }} <span class="kpi__unit">{{ $t("unity.ms") }}</span></div>
            <div class="kpi__hint">{{ $t("production.reporting") }} {{ reportingCount }} / {{ items.length }}</div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--green">
          <div class="kpi__badge"><v-icon size="20">mdi-wind-turbine-check</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.producers_running")) }}</div>
            <div class="kpi__value">{{ runningCount }} <span class="kpi__unit">/ {{ items.length }}</span></div>
            <div class="kpi__hint">{{ limitedCount }} {{ $t("production.limited") }}</div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--amber">
          <div class="kpi__badge"><v-icon size="20">mdi-speedometer-slow</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.last_underproduction")) }}</div>
            <div class="kpi__value">{{ underprodCount }} <span class="kpi__unit">{{ $t("production.current_underproduction") }}</span></div>
            <div class="kpi__hint">{{ alarmCount }} {{ $t("production.alarm_ongoing") }}</div>
          </div>
        </v-card>
      </div>

      <!-- Grafana panels -->
      <v-row dense class="mt-1">
        <v-col cols="12">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="primary">mdi-chart-areaspline</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.station_power_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaWindPowerStationPanelUrl(power_station_id, GRAFANA_WIND_PANELS.station_power, range)"
              :href="grafanaWindPowerStationUrl(power_station_id, range)"
              :title="$t('machine.station_power_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaWindPowerStationUrl(power_station_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-chart-areaspline</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
              <span class="text-caption text-medium-emphasis">{{ $t("machine.embed_disabled") }}</span>
            </a>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" color="primary">mdi-chart-bar-stacked</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t("machine.station_chart")) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaWindPowerStationPanelUrl(power_station_id, GRAFANA_WIND_PANELS.station_per_producer, range)"
              :href="grafanaWindPowerStationUrl(power_station_id, range)"
              :title="$t('machine.station_chart')"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaWindPowerStationUrl(power_station_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">mdi-chart-bar-stacked</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
            </a>
          </v-card>
        </v-col>
      </v-row>

      <!-- Producers of the station -->
      <v-card flat class="table-card">
        <div class="table-card__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-wind-turbine</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $filters.capitalize($t("machine.producers", 2)) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ items.length }}</v-chip>
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" height="2"></v-progress-linear>
        <!-- Phone: one card per producer -->
        <div class="m-list d-md-none">
          <v-card
            v-for="item in items"
            :key="`m-${item.producer_id}`"
            flat
            class="m-card"
            :class="{ 'm-card--nodata': !item.time }"
            @click="openProducer(item)"
          >
            <span class="m-card__bar" :class="freshnessClass(item.created_at) || 'fresh'"></span>
            <div class="m-card__body">
              <div class="m-card__head">
                <div class="min-width-0">
                  <div class="m-card__name">{{ $filters.capitalize(item.display_name) }}</div>
                  <div class="m-card__sub">{{ item.producer_type }}</div>
                </div>
                <span class="status-pill ml-auto" :class="item.running ? 'status-pill--on' : 'status-pill--off'">
                  <span class="status-pill__dot"></span>
                  {{ $filters.capitalize($t(item.running ? "production.running" : "production.stopped")) }}
                </span>
              </div>
              <div class="m-card__grid">
                <div>
                  <div class="m-stat__label">{{ $t("telemetry.power") }}</div>
                  <div class="m-stat__value">{{ $filters.formatFloat(item.power) }} <span class="m-stat__unit">{{ $t("unity.kw") }}</span></div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.wind") }}</div>
                  <div class="m-stat__value">
                    <template v-if="hasValue(item.wind)">{{ $filters.formatFloat(item.wind) }} <span class="m-stat__unit">{{ $t("unity.ms") }}</span></template>
                    <span v-else class="text-disabled">&ndash;</span>
                  </div>
                </div>
                <div>
                  <div class="m-stat__label">{{ $t("production.dispo") }}</div>
                  <div class="m-stat__value">{{ $filters.formatPercentage(item.dispo) }} <span class="m-stat__unit">%</span></div>
                </div>
              </div>
              <div class="m-card__foot">
                <span class="fresh-chip" :class="freshnessClass(item.created_at) || 'fresh'">
                  <template v-if="item.created_at">{{ relativeTime(item.created_at) }}</template>
                  <template v-else>{{ $t("production.no_telemetry") }}</template>
                </span>
                <span class="status-icons ml-auto">
                  <v-icon v-if="item.limitation_ongoing" size="small">mdi-tortoise</v-icon>
                  <v-icon v-if="hasAlarm(item)" size="small" color="error">mdi-bell-alert</v-icon>
                  <v-icon v-if="item.current_underprod" size="small" color="warning">mdi-speedometer-slow</v-icon>
                </span>
              </div>
            </div>
            <v-icon size="small" class="m-card__chevron">mdi-chevron-right</v-icon>
          </v-card>
          <div v-if="!items.length && !loading" class="text-center text-medium-emphasis py-6">
            {{ $filters.capitalize($t("production.no_producer")) }}
          </div>
        </div>

        <v-table class="wind-table d-none d-md-block" density="comfortable">
          <thead>
            <tr>
              <th class="text-left">{{ $filters.capitalize($t("production.name")) }}</th>
              <th class="text-center">{{ $filters.capitalize($t("generic.status")) }}</th>
              <th class="text-center">{{ $filters.capitalize($t("production.power")) }}</th>
              <th class="text-center">{{ $filters.capitalize($t("production.wind")) }}</th>
              <th class="text-center">{{ $filters.capitalize($t("production.dispo")) }} / {{ $t("production.dispo_temps") }}</th>
              <th class="text-center">{{ $filters.capitalize($t("production.last_underproduction")) }}</th>
              <th class="text-center">{{ $filters.capitalize($t("production.last_data")) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in items"
              :key="item.producer_id"
              class="producer-row"
              :title="$filters.capitalize($t('machine.open_producer'))"
              @click="openProducer(item)"
            >
              <td>
                <div class="producer-cell">
                  <span class="producer-bar" :class="freshnessClass(item.created_at) || 'fresh'"></span>
                  <div class="min-width-0">
                    <div class="producer-name">{{ $filters.capitalize(item.display_name) }}</div>
                    <div class="text-caption text-medium-emphasis">{{ item.producer_type }}</div>
                  </div>
                </div>
              </td>
              <td class="text-center">
                <span class="status-pill" :class="item.running ? 'status-pill--on' : 'status-pill--off'">
                  <span class="status-pill__dot"></span>
                  {{ $filters.capitalize($t(item.running ? "production.running" : "production.stopped")) }}
                </span>
                <div class="status-icons">
                  <v-icon v-if="item.limitation_ongoing" size="small">mdi-tortoise</v-icon>
                  <v-icon v-if="hasAlarm(item)" size="small" color="error">mdi-bell-alert</v-icon>
                  <v-icon v-if="item.current_underprod" size="small" color="warning">mdi-speedometer-slow</v-icon>
                </div>
              </td>
              <td class="text-center">
                <span class="font-weight-bold">{{ $filters.formatFloat(item.power) }}</span>
                <span class="text-caption text-medium-emphasis"> {{ $t("unity.kw") }}</span>
                <span v-if="hasValue(item.theoretical_prod)" class="text-caption text-medium-emphasis"> / {{ $filters.formatFloat(item.theoretical_prod) }}</span>
              </td>
              <td class="text-center">
                <template v-if="hasValue(item.wind)">{{ $filters.formatFloat(item.wind) }} <span class="text-caption text-medium-emphasis">{{ $t("unity.ms") }}</span></template>
                <span v-else class="text-disabled">&ndash;</span>
              </td>
              <td class="text-center">
                {{ $filters.formatPercentage(item.dispo) }} % <span class="text-medium-emphasis">/ {{ $filters.formatPercentage(item.dispo_temps) }} %</span>
              </td>
              <td class="text-center">
                <span v-if="item.current_underprod" class="fresh-chip over_3h">{{ $filters.capitalize($t("production.current_underproduction")) }}</span>
                <span v-else-if="item.last_underprod" class="text-body-2">{{ $filters.capitalize(item.last_underprod) }}</span>
                <span v-else class="text-disabled">&ndash;</span>
              </td>
              <td class="text-center">
                <span v-if="item.created_at" class="fresh-chip" :class="freshnessClass(item.created_at) || 'fresh'">{{ relativeTime(item.created_at) }}</span>
                <span v-else class="fresh-chip over_5h">{{ $t("production.no_telemetry") }}</span>
              </td>
            </tr>
            <tr v-if="!items.length && !loading">
              <td colspan="7" class="text-center text-medium-emphasis py-6">{{ $filters.capitalize($t("production.no_producer")) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card>
    </template>

    <v-card v-else flat class="empty-card text-medium-emphasis text-body-2">
      <v-icon size="36" class="mb-2" color="grey">mdi-factory</v-icon>
      <div>{{ $filters.capitalize($t("machine.station_not_found")) }}</div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  isNone,
  grafanaWindPowerStationUrl,
  grafanaWindPowerStationPanelUrl,
  GRAFANA_WIND_PANELS,
  GRAFANA_EMBED_ENABLED
} from "@/functions.js";
import utils from "@/mixins/utils";
import GrafanaFrame from "@/components/GrafanaFrame.vue";

export default {
  components: { GrafanaFrame },
  mixins: [utils],

  computed: {
    power_station_id() {
      const id = Number(this.$route.query.power_station_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    stationName() {
      return this.items.length ? this.$filters.capitalize(this.items[0].power_station_name || "") : "";
    },

    ranges() {
      const h = this.$t("limitations.duration_units.hour");
      const d = this.$t("limitations.duration_units.day");
      return [
        { value: "now-24h", label: `24 ${h}` },
        { value: "now-7d", label: `7 ${d}` },
        { value: "now-30d", label: `30 ${d}` }
      ];
    },

    totalPower() {
      return this.items.reduce((acc, p) => acc + (Number(p.power) || 0), 0);
    },

    totalTheoretical() {
      return this.items.reduce((acc, p) => acc + (Number(p.theoretical_prod) || 0), 0);
    },

    avgWind() {
      const winds = this.items.filter((p) => this.hasValue(p.wind) && p.time).map((p) => Number(p.wind));
      return winds.length ? winds.reduce((a, b) => a + b, 0) / winds.length : 0;
    },

    runningCount() {
      return this.items.filter((p) => p.running).length;
    },

    reportingCount() {
      return this.items.filter((p) => !isNone(p.time)).length;
    },

    limitedCount() {
      return this.items.filter((p) => p.limitation_ongoing).length;
    },

    underprodCount() {
      return this.items.filter((p) => p.current_underprod).length;
    },

    alarmCount() {
      return this.items.filter((p) => this.hasAlarm(p)).length;
    }
  },

  methods: {
    grafanaWindPowerStationUrl,
    grafanaWindPowerStationPanelUrl,

    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    hasAlarm(item) {
      return !!item.alarm_ongoing && Object.keys(item.alarm_ongoing).length !== 0;
    },

    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) {
        return this.$t("production.minutes_ago", { n: minutes });
      }
      const hours = Math.round(minutes / 60);
      if (hours < 48) {
        return this.$t("production.hours_ago", { n: hours });
      }
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) {
        return "over_5h";
      }
      const last_ = moment(last);
      if (last_ < this.limit_5_hour) {
        return "over_5h";
      } else if (last_ < this.limit_3_hour) {
        return "over_3h";
      } else if (last_ < this.limit_1_hour) {
        return "over_1h";
      }
      return "";
    },

    openProducer(item) {
      this.$router.push({ name: "wind_producer", query: { producer_id: item.producer_id } });
    },

    load() {
      if (isNone(this.power_station_id)) {
        this.items = [];
        return;
      }
      this.loading = true;
      this.limit_1_hour = moment().subtract(1, "hours");
      this.limit_3_hour = moment().subtract(3, "hours");
      this.limit_5_hour = moment().subtract(5, "hours");
      this.$store
        .dispatch("telemetries/windCurrentFujin", { power_station_id: this.power_station_id })
        .then((items) => {
          this.items = Array.isArray(items) ? items : [];
        })
        .catch((error) => {
          console.error("wind station:", error);
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
          this.lastRefresh = moment();
        });
    }
  },

  watch: {
    power_station_id() {
      this.load();
    }
  },

  mounted() {
    this.load();
    this.intervalId = window.setInterval(() => {
      this.load();
    }, 10 * 60 * 1000);
  },

  unmounted() {
    window.clearInterval(this.intervalId);
  },

  data() {
    return {
      intervalId: undefined,
      loading: false,
      lastRefresh: null,
      range: "now-24h",
      items: [],
      limit_1_hour: moment().subtract(1, "hours"),
      limit_3_hour: moment().subtract(3, "hours"),
      limit_5_hour: moment().subtract(5, "hours"),
      GRAFANA_WIND_PANELS,
      GRAFANA_EMBED_ENABLED
    };
  }
};
</script>

<style scoped>
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1f9fd1 55%, #6b5b4f 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 8px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dff3f9;
  color: #0093bf;
}
.topbar__badge--on {
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--off {
  background: #fde7e7;
  color: #d32f2f;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__sub {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.range-toggle {
  border-color: rgba(0, 0, 0, 0.12);
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
.min-width-0 {
  min-width: 0;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--purple .kpi__badge {
  background: #ece6f7;
  color: #6a3fb5;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.graf-card {
  padding: 12px 16px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.graf-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.graf-frame {
  display: block;
  width: 100%;
  /* as tall as the window allows: about half the viewport, never cramped */
  height: clamp(340px, 48vh, 640px);
  border: 0;
  border-radius: 8px;
  background: #fafbfc;
}
.graf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 140px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 147, 191, 0.4);
  background: #f7fbfd;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}

.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.table-card__head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.wind-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
}
.wind-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.wind-table :deep(tr.producer-row) {
  cursor: pointer;
}
.wind-table :deep(tr.producer-row:hover td) {
  background-color: #f2f9fc;
}
.producer-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.producer-bar {
  display: inline-block;
  width: 4px;
  height: 30px;
  border-radius: 2px;
  flex-shrink: 0;
  background-color: currentColor;
}
.producer-name {
  font-weight: 600;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}
.status-pill--on {
  color: #2e7d32;
}
.status-pill--off {
  color: #d32f2f;
}
.status-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.status-icons {
  display: flex;
  justify-content: center;
  gap: 4px;
  min-height: 18px;
  margin-top: 2px;
}
.empty-card {
  margin-top: 10px;
  padding: 32px 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

/* Phone layout: cards instead of the table */
.m-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
}
.m-card {
  display: flex;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
.m-card--nodata {
  background: #fdecec;
}
.m-card__bar {
  width: 4px;
  border-radius: 2px;
  background: currentColor;
  flex-shrink: 0;
}
.m-card__body {
  flex: 1;
  min-width: 0;
}
.m-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.m-card__name {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.m-card__sub {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}
.m-card__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px 10px;
}
.m-stat__label {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
.m-stat__value {
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.m-stat__unit {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
}
.m-card__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.m-card__chevron {
  align-self: center;
  color: rgba(0, 0, 0, 0.26);
}

@media (max-width: 900px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .graf-frame {
    height: 220px !important;
  }
  .topbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }
  .range-toggle {
    width: 100%;
  }
  .range-toggle .v-btn {
    flex: 1;
  }
}
</style>
